import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Input,
  Button,
  Select,
  notification,
  Col,
  Row,
  Divider,
  Space,
  Empty,
  InputNumber,
  Alert,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  PoweroffOutlined,
  MinusCircleOutlined,
  SettingOutlined,
  LockOutlined,
  MoreOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { ProgressBar } from 'react-loader-spinner';
import lib from '../../../assets/utils/lib';
import { keysHaveEmptyValues } from '../../../assets/utils/Helpers';
import { states } from '../../../assets/utils/config';
import { RenewOrgPolicy } from '../../compoinents/policyRenew';
import PasswordManagementModal from './PasswordManagementModal';
import PlanRemovalBlockedModal from './PlanRemovalBlockedModal';
import ManageOrgPlansGuide from './ManageOrgPlansGuide';
import OrgPlanBandSetup from './OrgPlanBandSetup';
import { isPlanRemovalBlocked } from '../planRequestHelpers';
import {
  getOrgEffectiveSegment,
  assignableOrgPlanLabel,
  isAssignableOrgPlan,
} from '../../plans/planCatalogHelpers';

const { Option } = Select;
const { TextArea } = Input;

const fieldsUpdate = ['org_name', 'org_email', 'org_address', 'org_state', 'org_city'];

function enrollmentSegmentOptions(org) {
  const opts = org?.enrollment_segment_options;
  if (Array.isArray(opts) && opts.length) {
    return opts.map((o) => ({
      label: o.label ?? o.key ?? o.value,
      value: String(o.key ?? o.value).toLowerCase(),
    }));
  }
  return [
    { label: 'Corporate', value: 'corporate' },
    { label: 'SME', value: 'sme' },
  ];
}

function segmentResolution(org) {
  return org?.enrollment_segment_resolution || {};
}

export default function OrgAccountAdmin({
  organization,
  onOrganizationChange,
  onPlansChanged,
  canManagePlans = false,
  compact = false,
  /**
   * Per-action gates from resolveOrgActionAccess. Every one defaults to false:
   * this toolbar previously rendered Edit, Renew, Disable and Set password to
   * anyone who could open the screen, so an omitted prop must fail closed
   * rather than restore that.
   */
  canEdit = false,
  canSetStatus = false,
  canRenew = false,
  canSetPassword = false,
}) {
  const navigate = useNavigate();
  const renewRef = useRef(null);

  const [visibleEdit, setVisibleEdit] = useState(false);
  const [visiblePlanManagement, setVisiblePlanManagement] = useState(false);
  const [visibleAddPlan, setVisibleAddPlan] = useState(false);
  const [addPlanId, setAddPlanId] = useState(null);
  const [passwordOrg, setPasswordOrg] = useState(null);

  const [formUpdate, setFormUpdate] = useState({});
  const [formUpdateOriginal, setFormUpdateOriginal] = useState(null);

  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plans, setPlans] = useState([]);
  const [orgPlans, setOrgPlans] = useState([]);

  const [expandedBands, setExpandedBands] = useState(null); // plan id whose sub-band prices are open inline

  const [planRemovalBlocked, setPlanRemovalBlocked] = useState({
    open: false,
    policies: [],
    message: '',
  });

  const formManage = organization;

  useEffect(() => {
    if (!visiblePlanManagement || !organization?.org_social_no) return undefined;
    loadAssignablePlans();
    loadOrgPlans();
    return undefined;
  }, [visiblePlanManagement, organization?.org_social_no]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = () => {
    setVisibleEdit(false);
    setVisiblePlanManagement(false);
    setFormUpdate({});
    setFormUpdateOriginal(null);
  };

  const handleEdit = async () => {
    setFormUpdate({ ...organization });
    setFormUpdateOriginal({ ...organization });
    setVisibleEdit(true);
    const socialNo = organization?.org_social_no;
    if (!socialNo) return;
    try {
      const parsed = await lib.getCorporation(socialNo);
      if (parsed.ok && parsed.data) {
        const enriched = {
          ...organization,
          ...parsed.data,
          enrollment_segment_options: parsed.data.enrollment_segment_options
            || parsed.enrollment_segment_options
            || organization.enrollment_segment_options,
          enrollment_segment_taxonomy: parsed.data.enrollment_segment_taxonomy
            || parsed.enrollment_segment_taxonomy
            || organization.enrollment_segment_taxonomy,
          enrollment_segment_resolution: parsed.data.enrollment_segment_resolution
            || parsed.enrollment_segment_resolution
            || organization.enrollment_segment_resolution,
        };
        setFormUpdate(enriched);
        setFormUpdateOriginal(enriched);
        onOrganizationChange?.(enriched);
      }
    } catch (_) {
      /* keep list-row snapshot */
    }
  };

  const handleUpdate = async () => {
    if (keysHaveEmptyValues(formUpdate, fieldsUpdate)) {
      notification.error({
        message: 'Please fill all the fields',
        placement: 'topLeft',
      });
      return;
    }
    const socialNo = formUpdate?.org_social_no || formUpdateOriginal?.org_social_no;
    if (!socialNo) {
      notification.error({ message: 'Organization social number is missing', placement: 'topLeft' });
      return;
    }
    const patch = lib.diffOrgProfilePatch(formUpdate, formUpdateOriginal || {});
    if (!Object.keys(patch).length) {
      notification.info({ message: 'No changes to save', placement: 'topLeft' });
      return;
    }
    const parsed = await lib.patchCorporation(socialNo, patch);
    if (parsed.ok) {
      notification.success({
        message: parsed.message || 'Organization updated successfully',
        placement: 'topLeft',
      });
      handleCancel();
      onOrganizationChange?.({
        ...organization,
        ...patch,
        ...(parsed.data || {}),
        ...(parsed.enrollment_segment_resolution
          ? { enrollment_segment_resolution: parsed.enrollment_segment_resolution }
          : {}),
      });
    } else {
      notification.error({
        message: parsed.message || 'Failed to update organization',
        placement: 'topLeft',
      });
    }
  };

  const handleStatus = async () => {
    const socialNo = organization?.org_social_no;
    if (!socialNo) {
      notification.error({ message: 'Organization social number is missing', placement: 'topLeft' });
      return;
    }
    const nextStatus = !parseInt(organization.org_status, 10) ? 1 : 0;
    const parsed = await lib.patchCorporation(socialNo, { org_status: nextStatus });
    if (parsed.ok) {
      notification.success({
        message: parsed.message || 'Organization status updated',
        placement: 'topLeft',
      });
      onOrganizationChange?.({
        ...organization,
        org_status: nextStatus,
        ...(parsed.data || {}),
      });
    } else {
      notification.error({
        message: parsed.message || 'Failed to update status',
        placement: 'topLeft',
      });
    }
  };

  const handlePassword = () => {
    setPasswordOrg(organization);
  };

  const openRenew = () => {
    setTimeout(() => renewRef.current?.open?.(), 0);
  };

  const loadOrgPlans = async (flag = 0) => {
    const socialNo = formManage?.org_social_no;
    if (!socialNo) {
      setOrgPlans([]);
      return;
    }

    setLoadingPlans(!Boolean(flag));
    try {
      const resp = await lib.getOrganizationPlanCatalog(socialNo);
      const catalog = lib.parseOrganizationPlanCatalog(resp);
      if (!catalog.ok) {
        notification.error({
          message: catalog.message || 'Failed to load organization plans',
          placement: 'topLeft',
        });
        setOrgPlans([]);
        return;
      }
      setOrgPlans(catalog.rows);
    } catch (e) {
      notification.error({
        message: e?.message || 'Failed to load organization plans',
        placement: 'topLeft',
      });
      setOrgPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadAssignablePlans = () => {
    const effectiveSegment = getOrgEffectiveSegment(organization);
    lib.getAdminPlansCatalog({ scope: 'assignable' })
      .then((resp) => {
        const parsed = lib.parseAdminPlansList(resp);
        if (!parsed.ok) {
          notification.error({
            message: parsed.message || 'Failed to load assignable plans',
            placement: 'topLeft',
          });
          setPlans([]);
          return;
        }
        setPlans(parsed.plans.filter((p) => isAssignableOrgPlan(p, effectiveSegment)));
      })
      .catch(() => setPlans([]));
  };

  const handleManage = () => {
    setVisiblePlanManagement(false);
  };

  const handlePlanManagement = () => {
    if (!canManagePlans) {
      notification.warning({
        message: 'Permission required',
        description: 'You need corporations.manage (or admin.*) to change organization plan types.',
        placement: 'topLeft',
      });
      return;
    }
    setVisiblePlanManagement(true);
  };

  const showAddplan = () => {
    loadAssignablePlans();
    setAddPlanId(null);
    setVisibleAddPlan(true);
  };


  const showPlanRemovalBlocked = (parsed, fallbackMessage) => {
    setPlanRemovalBlocked({
      open: true,
      policies: parsed?.blockingPolicies ?? parsed?.blocking_policies ?? parsed?.data?.blocking_policies ?? [],
      message: parsed?.message || fallbackMessage,
    });
  };




  const removeOrgPlan = (plan) => {
    const socialNo = formManage?.org_social_no;
    const planId = plan?.plan_id ?? plan?.id;
    if (!socialNo || planId == null) {
      notification.error({ message: 'Missing organization or plan', placement: 'topLeft' });
      return;
    }

    Modal.confirm({
      title: `Remove ${plan.plan_name || 'plan'}?`,
      content: 'This applies immediately (admin bypass). HR-submitted changes use the plan approval queue instead.',
      okText: 'Remove plan',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const checkParsed = await lib.checkOrgPlanRemoval(socialNo, planId).then(lib.parseApiEnvelope);
        if (checkParsed.ok && checkParsed.data?.can_remove === false) {
          showPlanRemovalBlocked(
            { blockingPolicies: checkParsed.data?.blocking_policies, message: checkParsed.message },
            'Cannot remove — active members have care history on this plan.',
          );
          return;
        }

        setLoadingPlans(true);
        try {
          const parsed = await lib.removeOrganizationPlan(socialNo, planId);
          if (parsed.ok) {
            notification.success({ message: parsed.message || 'Plan removed', placement: 'topLeft' });
            await loadOrgPlans(1);
            onPlansChanged?.();
            return;
          }
          if (isPlanRemovalBlocked(parsed)) {
            showPlanRemovalBlocked(parsed, 'Cannot remove — active members have care history on this plan.');
            return;
          }
          notification.error({ message: parsed.message || 'Remove failed', placement: 'topLeft' });
        } finally {
          setLoadingPlans(false);
        }
      },
    });
  };

  const navigateToPlanRequests = () => {
    handleCancel();
    if (organization.hash) {
      navigate(`/corporations/users/${organization.hash}?tab=plan_requests`, { state: organization });
      return;
    }
    const orgId = organization.org_id;
    const orgName = organization.org_name || '';
    if (orgId) {
      navigate(`/plans?tab=plan-requests&org_id=${encodeURIComponent(orgId)}&org_name=${encodeURIComponent(orgName)}`);
    } else {
      navigate('/plans?tab=plan-requests');
    }
  };

  if (!organization) return null;

  const isActive = parseInt(organization.org_status, 10) === 1;

  const destructiveItems = [
    ...(canSetStatus ? [{
      key: 'status',
      label: isActive ? 'Disable' : 'Enable',
      icon: <PoweroffOutlined />,
      danger: isActive,
      onClick: handleStatus,
    }] : []),
    ...(canSetPassword ? [{
      key: 'password',
      label: 'Set password',
      icon: <LockOutlined />,
      onClick: handlePassword,
    }] : []),
  ];

  const actionItems = [
    ...(canEdit ? [{
      key: 'edit',
      label: 'Edit organization',
      icon: <EditOutlined />,
      onClick: handleEdit,
    }] : []),
    ...(canManagePlans ? [{
      key: 'plans',
      label: 'Manage plans',
      icon: <SettingOutlined />,
      onClick: handlePlanManagement,
    }] : []),
    ...(canRenew ? [{
      key: 'renew',
      label: 'Renew policy',
      icon: <CalendarOutlined />,
      onClick: openRenew,
    }] : []),
    // The divider only earns its place when there is something on both sides.
    ...(destructiveItems.length && (canEdit || canManagePlans || canRenew)
      ? [{ type: 'divider' }]
      : []),
    ...destructiveItems,
  ];

  // Nothing to administer — render no toolbar at all rather than an empty menu.
  if (!actionItems.length) return null;

  const toolbar = compact ? (
    <Dropdown menu={{ items: actionItems }} trigger={['click']}>
      <Button type="default" icon={<MoreOutlined />}>
        Account
      </Button>
    </Dropdown>
  ) : (
    <Space wrap>
      {canEdit ? (
        <Button icon={<EditOutlined />} onClick={handleEdit}>
          Edit organization
        </Button>
      ) : null}
      {canManagePlans ? (
        <Button icon={<SettingOutlined />} onClick={handlePlanManagement}>
          Manage plans
        </Button>
      ) : null}
      {canRenew ? (
        <Button icon={<CalendarOutlined />} onClick={openRenew}>
          Renew policy
        </Button>
      ) : null}
      {canSetStatus ? (
        <Button
          icon={<PoweroffOutlined />}
          danger={isActive}
          onClick={handleStatus}
        >
          {isActive ? 'Disable' : 'Enable'}
        </Button>
      ) : null}
      {canSetPassword ? (
        <Button icon={<LockOutlined />} onClick={handlePassword}>
          Set password
        </Button>
      ) : null}
    </Space>
  );

  return (
    <>
      {toolbar}

      <Modal
        title="Edit Detail"
        open={visibleEdit}
        onOk={handleUpdate}
        onCancel={handleCancel}
        forceRender
        width={560}
      >
        <Row gap={6} style={{ marginBottom: 8 }}>
          <Col span={8}>Name</Col>
          <Col span={16}>
            <Input value={formUpdate?.org_name} onChange={(e) => setFormUpdate((d) => ({ ...d, org_name: e.target.value }))} />
          </Col>
        </Row>
        <Row gap={6} style={{ marginBottom: 8 }}>
          <Col span={8}>Email</Col>
          <Col span={16}>
            <Input value={formUpdate?.org_email} onChange={(e) => setFormUpdate((d) => ({ ...d, org_email: e.target.value }))} />
          </Col>
        </Row>
        <Row gap={6} style={{ marginBottom: 8 }}>
          <Col span={8}>Phone</Col>
          <Col span={16}>
            <Input value={formUpdate?.org_phone} onChange={(e) => setFormUpdate((d) => ({ ...d, org_phone: e.target.value }))} />
          </Col>
        </Row>
        <Row gap={6} style={{ marginBottom: 8 }}>
          <Col span={8}>Address</Col>
          <Col span={16}>
            <TextArea value={formUpdate?.org_address} onChange={(e) => setFormUpdate((d) => ({ ...d, org_address: e.target.value }))} rows={3} />
          </Col>
        </Row>
        <Row gap={6} style={{ marginBottom: 8 }}>
          <Col span={8}>State</Col>
          <Col span={16}>
            <Select options={states} value={formUpdate?.org_state} onChange={(e) => setFormUpdate((d) => ({ ...d, org_state: e }))} style={{ width: '100%' }} />
          </Col>
        </Row>
        <Row gap={6} style={{ marginBottom: 8 }}>
          <Col span={8}>City</Col>
          <Col span={16}>
            <Input value={formUpdate?.org_city} onChange={(e) => setFormUpdate((d) => ({ ...d, org_city: e.target.value }))} />
          </Col>
        </Row>
        <Divider style={{ margin: '12px 0' }} />
        <Row gap={6} style={{ marginBottom: 8 }}>
          <Col span={8}>Declared headcount</Col>
          <Col span={16}>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={formUpdate?.org_declared_headcount != null && formUpdate?.org_declared_headcount !== ''
                ? Number(formUpdate.org_declared_headcount)
                : null}
              onChange={(v) => setFormUpdate((d) => ({ ...d, org_declared_headcount: v }))}
              placeholder="Optional — used for SME / corporate classification"
            />
          </Col>
        </Row>
        <Row gap={6} style={{ marginBottom: 8 }}>
          <Col span={8}>Segment override</Col>
          <Col span={16}>
            <Select
              allowClear
              placeholder="Use cycle-locked segment (no override)"
              style={{ width: '100%' }}
              options={enrollmentSegmentOptions(organization)}
              value={formUpdate?.org_enrollment_segment_override || undefined}
              onChange={(v) => setFormUpdate((d) => ({
                ...d,
                org_enrollment_segment_override: v == null ? null : v,
              }))}
            />
          </Col>
        </Row>
        {(() => {
          const res = segmentResolution(organization);
          const effective = getOrgEffectiveSegment(organization) || res.effective_segment;
          const label = organization?.enrollment_segment_label
            || String(effective || '').toUpperCase();
          if (!effective && !res.computed_segment) return null;
          return (
            <Alert
              type={res.pending_reclassification ? 'warning' : 'info'}
              showIcon
              style={{ marginTop: 8 }}
              message={`Effective segment: ${label || effective}`}
              description={(
                <Space direction="vertical" size={2}>
                  <span>
                    Source:{' '}
                    {res.effective_source === 'override' ? 'Manual override' : 'Locked until renewal'}
                  </span>
                  {res.computed_segment ? (
                    <span>
                      Rules currently say: {res.computed_segment}
                      {Array.isArray(res.computed_reasons) && res.computed_reasons.length
                        ? ` (${res.computed_reasons.join('; ')})`
                        : ''}
                    </span>
                  ) : null}
                  {res.pending_reclassification ? (
                    <span>
                      Will become <strong>{res.pending_segment}</strong> at next renewal unless overridden.
                    </span>
                  ) : null}
                </Space>
              )}
            />
          );
        })()}
      </Modal>

      <Modal
        title={<>Plan Management - <em>{formManage?.org_name}</em></>}
        open={visiblePlanManagement}
        onOk={handleManage}
        onCancel={handleCancel}
        forceRender
        width={1100}
        footer={[
          <Button key="add" type="dashed" danger onClick={showAddplan} icon={<PlusOutlined />}>Add Plan</Button>,
          <Button key="done" type="primary" onClick={handleManage}>Done</Button>,
        ]}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Admin bypass — changes apply immediately"
          description={(
            <span>
              HR plan add/remove requests go through the{' '}
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={navigateToPlanRequests}
              >
                plan approval queue
              </Button>
              . Use this screen for internal ops only.
              {getOrgEffectiveSegment(organization) ? (
                <>
                  {' '}Assignable plans are filtered to the org’s effective segment (
                  <strong>{organization?.enrollment_segment_label || getOrgEffectiveSegment(organization)}</strong>
                  ).
                </>
              ) : null}
            </span>
          )}
        />
        <ManageOrgPlansGuide />
        <Divider style={{ marginTop: 0 }} />
        {loadingPlans ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ProgressBar
              height="80"
              width="80"
              ariaLabel="progress-bar-loading"
              wrapperStyle={{}}
              wrapperClass="progress-bar-wrapper"
              borderColor="#F4442E"
              barColor="#51E5FF"
              visible={loadingPlans}
            />
          </div>
        ) : (
          <>
            {orgPlans.length ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {orgPlans.map((plan) => {
                  const pid = plan.plan_id ?? plan.id;
                  const open = expandedBands === pid;
                  return (
                    <div key={pid} style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: '8px 10px' }}>
                      <Row gap={4} style={{ width: '100%' }} align="middle">
                        <Col span={13}><strong>{plan?.plan_name}</strong><div style={{ fontSize: 11, color: '#888' }}>{plan?.plan_description}</div></Col>
                        <Col span={11} style={{ textAlign: 'right' }}>
                          <Space wrap>
                            <Button type={open ? 'primary' : 'default'} onClick={() => setExpandedBands(open ? null : pid)} disabled={loadingPlans}>
                              {open ? 'Hide bands & pricing' : 'Bands & pricing'}
                            </Button>
                            <Button type="primary" danger icon={<MinusCircleOutlined />} onClick={() => removeOrgPlan(plan)} disabled={loadingPlans}>Remove</Button>
                          </Space>
                        </Col>
                      </Row>
                      {open ? (
                        <OrgPlanBandSetup
                          mode="manage"
                          socialNo={formManage?.org_social_no}
                          orgId={organization?.org_id}
                          planId={pid}
                          planName={plan?.plan_name}
                          onSaved={() => { loadOrgPlans(1); onPlansChanged?.(); }}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </Space>
            ) : (
              <Empty description={<strong><em>There are no plans for this organization</em></strong>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </>
        )}
        <Divider style={{ marginBottom: 0 }} />
      </Modal>

      <Modal
        title="Add a plan"
        open={visibleAddPlan}
        onCancel={() => setVisibleAddPlan(false)}
        destroyOnClose
        footer={null}
        width={900}
      >
        <ManageOrgPlansGuide compact />
        <div style={{ margin: '12px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>1. Plan</div>
          <Select
            style={{ width: '100%' }}
            placeholder="Select a plan"
            showSearch
            optionFilterProp="children"
            value={addPlanId}
            onChange={(v) => setAddPlanId(v)}
          >
            {plans.map((item) => {
              const id = item.plan_id ?? item.id;
              return (
                <Option key={id} value={id} disabled={Boolean(orgPlans.find((itm) => (itm?.plan_id ?? itm?.id) === id))}>
                  {assignableOrgPlanLabel(item)}
                </Option>
              );
            })}
          </Select>
        </div>
        {addPlanId ? (
          <>
            <div style={{ fontWeight: 600, margin: '12px 0 0' }}>2. Bands and pricing</div>
            <OrgPlanBandSetup
              key={addPlanId}
              mode="add"
              socialNo={formManage?.org_social_no}
              orgId={organization?.org_id}
              planId={addPlanId}
              planName={plans.find((p) => (p.plan_id ?? p.id) === addPlanId)?.plan_name}
              onCancel={() => setVisibleAddPlan(false)}
              onSaved={async () => {
                setVisibleAddPlan(false);
                setAddPlanId(null);
                await loadOrgPlans(1);
                onPlansChanged?.();
              }}
            />
          </>
        ) : (
          <div style={{ color: '#888', fontSize: 13 }}>Choose a plan to set up its bands and prices for this organisation.</div>
        )}
      </Modal>

      <RenewOrgPolicy
        ref={renewRef}
        key={organization.org_id}
        data={organization}
        action={(updated) => {
          if (updated && typeof updated === 'object') {
            onOrganizationChange?.({ ...organization, ...updated });
          }
        }}
        viewType="tile"
      />

      <Modal
        title={`Set Password - ${passwordOrg?.org_name || 'Organization'}`}
        open={Boolean(passwordOrg)}
        onCancel={() => setPasswordOrg(null)}
        footer={null}
        width={600}
        destroyOnClose
      >
        {passwordOrg ? (
          <PasswordManagementModal
            organization={passwordOrg}
            onClose={() => setPasswordOrg(null)}
          />
        ) : null}
      </Modal>

      <PlanRemovalBlockedModal
        open={planRemovalBlocked.open}
        blockingPolicies={planRemovalBlocked.policies}
        message={planRemovalBlocked.message}
        onClose={() => setPlanRemovalBlocked({ open: false, policies: [], message: '' })}
      />
    </>
  );
}
